import { Box, Button, Flex, Loading, Text } from "@100mslive/react-ui";
import {
  Container,
  ContentBody,
  ContentHeader,
  ErrorText,
  RecordStream,
} from "./Common";
import {
  EndStreamIcon,
  EyeOpenIcon,
  GoLiveIcon,
  InfoIcon,
  LinkIcon,
  PeopleIcon,
  SupportIcon,
  WrenchIcon,
} from "@100mslive/react-icons";
import { Fragment, useCallback, useEffect, useState } from "react";
import {
  selectAppData,
  selectHLSState,
  selectRoomID,
  useHMSActions,
  useHMSStore,
  useRecordingStreaming,
} from "@100mslive/react-sdk";

import { APP_DATA } from "../../common/constants";
import { ROLES } from "../../common/roles";
import { getDefaultMeetingUrl } from "../../common/utils";
import requestService from "../../services/requestService";
import { useFilteredRoles } from "../../common/hooks";
import { useSetAppDataByKey } from "../AppData/useUISettings";
import { v4 as uuid } from "uuid";

const getCardData = (roleName, roomId, token) => {
  let data = {};
  const formattedRoleName = roleName[0].toUpperCase() + roleName.slice(1);

  switch (roleName) {
    case "broadcaster": {
      data = {
        title: formattedRoleName,
        content:
          "Người phát sóng có thể phát trực tiếp âm thanh hoặc video, quản lý hình thức phát trực tiếp và điều khiển phòng thông qua Live HODO.",
        icon: <SupportIcon />,
      };
      break;
    }
    case "hls-viewer": {
      data = {
        title: "HLS Viewer",
        content:
          "Người xem có thể xem và gửi tin nhắn trò chuyện nhưng cần phải là người phát sóng để tham gia bằng âm thanh hoặc video.",
        icon: <EyeOpenIcon />,
      };
      break;
    }
    default:
      data = {
        title: formattedRoleName,
        content: `${formattedRoleName} được tùy chỉnh với các quyền cụ thể, điều này sẽ xác định cách nó tương tác với phòng này.`,
        icon: <WrenchIcon />,
        order: 1,
      };
  }
  // data["link"] = `/redirect.html?token=${token}&room_id=${roomId}&role=${roleName}`;
  data["link"] = `/${roomId}/${roleName}`;
  return data;
};

const Card = ({ title, icon, link, content, isHLSRunning, order = 0 }) => {
  const [copied, setCopied] = useState(false);
  return isHLSRunning ? (
    <Box
      key={title}
      css={{
        backgroundColor: "$surfaceLight",
        padding: "$10",
        order,
        borderRadius: "$2",
      }}
    >
      <Flex align="center" gap="2" css={{ color: "$primaryLight" }}>
        {icon}
        <Text variant="h6" css={{ fontWeight: "$semiBold" }}>
          {title}
        </Text>
      </Flex>
      <Text variant="sm" css={{ color: "$textMedEmp", mt: "$6" }}>
        {content}
      </Text>
      <Button
        variant="standard"
        onClick={() => {
          navigator.clipboard.writeText(`${window.location.origin}${link}`);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        css={{ w: "100%", r: "$1", mt: "$10", fontWeight: "$semiBold" }}
        icon
      >
        {copied ? (
          <>Liên kết đã được sao chép!</>
        ) : (
          <>
            <LinkIcon style={{ color: "inherit" }} />
            Sao chép liên kết mời tham gia
          </>
        )}
      </Button>
    </Box>
  ) : null;
};

export const HLSStreaming = ({ onBack }) => {
  const roleNames = useFilteredRoles();
  const roomId = useHMSStore(selectRoomID);
  const cards = roleNames
    .filter(roleName => roleName !== "hls-viewer")
    .map(roleName => getCardData(roleName, roomId));

  const { isHLSRunning } = useRecordingStreaming();
  const [showLinks, setShowLinks] = useState(false);

  const [isHLSStarted, setHLSStarted] = useSetAppDataByKey(APP_DATA.hlsStarted);

  const hlsState = useHMSStore(selectHLSState);

  useEffect(() => {
    (async () => {
      if (isHLSStarted && hlsState.running) {
        const hlsUrl = hlsState.variants[0]?.url;
        await handleStartLive(hlsUrl);
      }
    })();
  }, [isHLSStarted, hlsState]);

  const handleStartLive = useCallback(async hlsUrl => {
    const circle_id = Number(localStorage.getItem("circle_id"));
    const room_id = localStorage.getItem("room_id");

    const params = {
      circle_id,
      hls_url: hlsUrl || "",
      room_id,
    };
    const res = await requestService
      .notifyStartStream(params)
      .then(data => data.json());
    console.log(res);
  }, []);

  return !showLinks ? (
    <Container rounded>
      <ContentHeader
        title="Bắt đầu phát trực tiếp"
        content="Live HODO"
        onBack={onBack}
      />
      <ContentBody
        title="Phát trực tiếp"
        Icon={GoLiveIcon}
        removeVerticalPadding
      >
        Phát trực tiếp từ trình duyệt bằng bất kỳ thiết bị nào có nhiều máy chủ
        và nhắn tin theo thời gian thực, tất cả đều nằm trong nền tảng này.
      </ContentBody>
      {isHLSRunning ? <EndHLS setShowLinks={setShowLinks} /> : <StartHLS />}
    </Container>
  ) : (
    <Container rounded>
      <ContentHeader
        title="Mời người tham gia"
        content="Bắt đầu cuộc trò chuyện"
        onBack={() => setShowLinks(false)}
      />

      <Flex
        direction="column"
        css={{ gap: "$10", p: "$0 $10", overflowY: "auto", mb: "$10" }}
      >
        {cards.map(card => (
          <Card key={card.title} {...card} isHLSRunning={isHLSRunning} />
        ))}
      </Flex>
    </Container>
  );
};

const StartHLS = () => {
  const [record, setRecord] = useState(false);
  const [error, setError] = useState(false);
  const hmsActions = useHMSActions();
  const recordingUrl = useHMSStore(selectAppData(APP_DATA.recordingUrl));
  const [isHLSStarted, setHLSStarted] = useSetAppDataByKey(APP_DATA.hlsStarted);

  const startHLS = useCallback(
    async variants => {
      try {
        if (isHLSStarted) {
          return;
        }
        setHLSStarted(true);
        setError("");
        await hmsActions.startHLSStreaming({
          variants,
          recording: { hlsVod: record, singleFilePerLayer: record },
        });
      } catch (error) {
        if (error?.message?.includes("invalid input")) {
          await startHLS([
            { meetingURL: recordingUrl || getDefaultMeetingUrl() },
          ]);
          return;
        }
        setHLSStarted(false);
        setError(error?.message);
      }
    },
    [hmsActions, record, isHLSStarted, setHLSStarted, recordingUrl]
  );

  return (
    <Fragment>
      {/* <RecordStream
        record={record}
        setRecord={setRecord}
        testId="hls-recording"
      /> */}
      <Box css={{ p: "$4 $10" }}>
        <ErrorText error={error} />
        <Button
          data-testid="start_hls"
          css={{ w: "100%", r: "$0" }}
          icon
          onClick={() => startHLS()}
          disabled={isHLSStarted}
        >
          {isHLSStarted ? (
            <Loading size={24} color="currentColor" />
          ) : (
            <GoLiveIcon />
          )}
          {isHLSStarted ? "Đang phát..." : "Phát trực tiếp"}
        </Button>
      </Box>
      <Flex align="center" css={{ p: "$4 $10" }}>
        <Text>
          <InfoIcon width={16} height={16} />
        </Text>
        <Text variant="tiny" color="$textMedEmp" css={{ mx: "$8" }}>
          Bạn không thể bật lưu bản ghi khi đang phát trực tiếp, bạn sẽ phải
          dừng phát để bật lưu bản ghi.
        </Text>
      </Flex>
    </Fragment>
  );
};

const EndHLS = ({ setShowLinks }) => {
  const hmsActions = useHMSActions();

  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState("");
  const { isHLSRunning } = useRecordingStreaming();

  useEffect(() => {
    if (inProgress && !isHLSRunning) {
      setInProgress(false);
    }
  }, [inProgress, isHLSRunning]);

  const stopHLS = useCallback(async () => {
    try {
      setInProgress(true);
      await hmsActions.stopHLSStreaming().then(async () => {
        const circle_id = Number(localStorage.getItem("circle_id"));
        const room_id = localStorage.getItem("room_id");
        const params = {
          circle_id,
          room_id,
        };
        const res = await requestService
          .notifyStopStream(params)
          .then(data => data.json());
        console.log(res);
      });
    } catch (error) {
      setError(error?.message);
      setInProgress(false);
    }
  }, [hmsActions]);

  return (
    <Box css={{ p: "$4 $10" }}>
      <ErrorText error={error} />
      <Button
        data-testid="stop_hls"
        variant="danger"
        css={{ w: "100%", r: "$0", mt: "$8" }}
        icon
        loading={inProgress}
        disabled={inProgress}
        onClick={() => stopHLS()}
      >
        <EndStreamIcon />
        Kết thúc phát trực tiếp
      </Button>
      <Button
        icon
        css={{ w: "100%", r: "$0", mt: "$8" }}
        onClick={() => setShowLinks(true)}
      >
        <PeopleIcon /> Mời người tham gia
      </Button>
    </Box>
  );
};
